import ApiClient from "@/lib/api-client"
import vuexStore from "@/store"

// Short enough that a reused response can't be perceived as stale.
const READ_TTL_MS = 1000

const THROTTLE_BACKOFF_MS = 5000
const MAX_THROTTLE_BACKOFF_MS = 60000

type Entry = { promise: Promise<any>; expiresAt: number }

const stableStringify = (value: any): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null"
  }

  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`

  return `{${Object.keys(value)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`
}

// Authored code owns what it is handed, so each caller gets its own copy.
const clone = (value: any) =>
  value === null || typeof value !== "object"
    ? value
    : JSON.parse(JSON.stringify(value))

const throttledError = (): Error => {
  const error: any = new Error("Too many requests")
  error.response = { status: 429 }
  return error
}

export class ReadCache {
  private readonly reads = new Map<string, Entry>()
  private identity: string | null = null
  private throttledUntil = 0
  private throttleBackoff = THROTTLE_BACKOFF_MS

  get(endpoint: string, params: Record<string, any>): Promise<any> {
    this.syncIdentity()

    const key = `${endpoint}|${stableStringify(params)}`
    const cached = this.reads.get(key)
    if (cached && Date.now() < cached.expiresAt) {
      return cached.promise.then(clone)
    }

    if (Date.now() < this.throttledUntil) {
      return Promise.reject(throttledError())
    }

    this.prune()

    const promise = this.track(() =>
      ApiClient.get(endpoint, { params }).then((response: any) => response.data)
    )

    this.reads.set(key, { promise, expiresAt: Infinity })
    promise.then(
      () => this.expire(key, promise),
      () => this.evict(key, promise)
    )

    return promise.then(clone)
  }

  reset(): void {
    this.reads.clear()
    this.identity = null
    this.throttledUntil = 0
    this.throttleBackoff = THROTTLE_BACKOFF_MS
  }

  // A read issued mid-write describes the state before it, so the clear on
  // settle is as load-bearing as the one up front.
  write(run: () => Promise<any>): Promise<any> {
    this.syncIdentity()
    this.reads.clear()

    return this.track(run).finally(() => this.reads.clear())
  }

  private track<T>(run: () => Promise<T>): Promise<T> {
    return run()
      .then(result => {
        this.throttleBackoff = THROTTLE_BACKOFF_MS
        return result
      })
      .catch(error => {
        if (error?.response?.status === 429) this.openBreaker(error)
        throw error
      })
  }

  // Without this an entry keyed on a value that varies per interaction — a
  // search term, a page number — is pinned for the session.
  private prune(): void {
    const now = Date.now()
    this.reads.forEach((entry, key) => {
      if (now >= entry.expiresAt) this.reads.delete(key)
    })
  }

  private expire(key: string, promise: Promise<any>): void {
    const entry = this.reads.get(key)
    if (entry?.promise !== promise) return

    entry.expiresAt = Date.now() + READ_TTL_MS
  }

  private evict(key: string, promise: Promise<any>): void {
    if (this.reads.get(key)?.promise === promise) this.reads.delete(key)
  }

  // Cached reads belong to the member and app that made them.
  private syncIdentity(): void {
    const hasSession = vuexStore.getters["auth/hasSession"]
    const identity = [
      vuexStore.getters.app?.slug,
      hasSession ? vuexStore.getters["auth/currentUser"].email : "anonymous",
      hasSession ? vuexStore.getters["auth/authenticationToken"] : ""
    ].join("|")

    if (identity === this.identity) return

    this.identity = identity
    this.reads.clear()
  }

  private openBreaker(error: any): void {
    const retryAfter = Number(error.response?.headers?.["retry-after"]) * 1000
    const wait = retryAfter > 0 ? retryAfter : this.throttleBackoff

    this.throttledUntil = Date.now() + wait
    this.throttleBackoff = Math.min(
      this.throttleBackoff * 2,
      MAX_THROTTLE_BACKOFF_MS
    )
  }
}
